import { useEffect, useState } from 'react'
import axios from 'axios'
import { municipalitiesApi } from '@/services/municipalities'

/**
 * URL のホスト名から (apex/env を除いた) 自治体 subdomain を解決し、自治体名を引き当てるフック。
 *
 * apex は lifekyufu.jp 固定。`.stg` などの環境ラベルがあれば剥がした残りを subdomain とする。
 * DB 側の municipalities.subdomain は単段 (`dummy`) も多段 (`meguro.tokyo`) もあり得る。
 *
 * 例:
 *   meguro.tokyo.stg.lifekyufu.jp → subdomain="meguro.tokyo" → 「目黒区」
 *   suginami.tokyo.stg.lifekyufu.jp → subdomain="suginami.tokyo" → 「杉並区」
 *   dummy.stg.lifekyufu.jp        → subdomain="dummy" → 「ダミー自治体(保守対応用)」
 *   meguro.tokyo.lifekyufu.jp     → subdomain="meguro.tokyo" → 「目黒区」 (本番想定)
 *   stg.lifekyufu.jp              → 共通ホスト扱い → null（自治体未特定）
 *   lifekyufu.jp                  → 共通ホスト扱い → null
 *   localhost / 127.0.0.1         → 開発環境扱い → null
 *
 * ログイン前後どちらでも使える（バックエンドは /api/public/** を permitAll 済み）。
 * 同一 subdomain への問い合わせは sessionStorage にキャッシュし、ページ遷移しても再フェッチしない。
 */

export interface MunicipalityFromHost {
  /** ホスト名から抽出した subdomain ラベル。共通ホストの場合 null。 */
  subdomain: string | null
  /** 解決された自治体名。未解決 / 404 の場合 null。 */
  name: string | null
  /** 解決された都道府県名。未解決の場合 null。 */
  prefecture: string | null
  /** API 問い合わせ中 true。 */
  loading: boolean
  /**
   * 自治体 subdomain 付きホストで、公開 API が 404（無効または未登録）のとき true。
   * 共通ホスト、有効な自治体、通信失敗では false。通信失敗でログイン画面を隠さない。
   */
  closed: boolean
}

// アプリの apex 域 (本番 / stg 共通)。
const APEX_DOMAIN = 'lifekyufu.jp'
// apex の直前に付く環境ラベル。これらは subdomain から剥がす ("dummy.stg.lifekyufu.jp" → "dummy")。
const ENV_LABELS = new Set(['stg', 'dev'])

const CACHE_PREFIX = 'pams_muni_by_subdomain_v2__'
const NULL_SENTINEL = '__not_found__'

function extractSubdomain(hostname: string): string | null {
  if (!hostname) return null
  const lower = hostname.toLowerCase()
  // IPv4 / IPv6 リテラルは subdomain なし。
  if (/^[\d.]+$/.test(lower) || lower.includes(':')) return null
  // apex (lifekyufu.jp) 配下でなければ localhost / dev 環境とみなし問い合わせない。
  if (lower !== APEX_DOMAIN && !lower.endsWith('.' + APEX_DOMAIN)) return null
  // apex を剥がす: "meguro.tokyo.stg.lifekyufu.jp" → "meguro.tokyo.stg"、"lifekyufu.jp" → ""
  let before = lower === APEX_DOMAIN
    ? ''
    : lower.slice(0, -('.' + APEX_DOMAIN).length)
  // 末尾の環境ラベル (".stg" / ".dev" や、apex 直上の bare "stg") があれば剥がす。
  // 本番では環境ラベルが無く `meguro.tokyo.lifekyufu.jp` などがそのまま subdomain になる。
  const lastDot = before.lastIndexOf('.')
  const lastLabel = lastDot >= 0 ? before.slice(lastDot + 1) : before
  if (ENV_LABELS.has(lastLabel)) {
    before = lastDot >= 0 ? before.slice(0, lastDot) : ''
  }
  return before || null
}

function readCache(subdomain: string): { name: string; prefecture: string | null } | null | undefined {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + subdomain)
    if (raw === null) return undefined
    if (raw === NULL_SENTINEL) return null
    const parsed = JSON.parse(raw) as { name: string; prefecture: string | null }
    return parsed
  } catch {
    return undefined
  }
}

function writeCache(subdomain: string, value: { name: string; prefecture: string | null } | null) {
  try {
    sessionStorage.setItem(
      CACHE_PREFIX + subdomain,
      value === null ? NULL_SENTINEL : JSON.stringify(value),
    )
  } catch {
    /* sessionStorage 不可（privacy mode 等）でも無視 */
  }
}

export function useMunicipalityFromHost(): MunicipalityFromHost {
  // hostname は SPA 内では事実上不変なので useState の初期化関数で 1 回読み取る。
  const [state, setState] = useState<MunicipalityFromHost>(() => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
    const subdomain = extractSubdomain(hostname)
    if (!subdomain) {
      return { subdomain: null, name: null, prefecture: null, loading: false, closed: false }
    }
    const cached = readCache(subdomain)
    if (cached !== undefined) {
      return {
        subdomain,
        name: cached ? cached.name : null,
        prefecture: cached ? cached.prefecture : null,
        loading: false,
        closed: cached === null,
      }
    }
    // キャッシュなし → effect で fetch する。
    return { subdomain, name: null, prefecture: null, loading: true, closed: false }
  })

  const { subdomain, loading } = state

  useEffect(() => {
    // 初期 state で loading=true となるケース（subdomain あり・キャッシュなし）のみ API を呼ぶ。
    if (!subdomain || !loading) return
    let cancelled = false
    municipalitiesApi
      .publicGetBySubdomain(subdomain)
      .then((res) => {
        if (cancelled) return
        const value = { name: res.data.name, prefecture: res.data.prefecture }
        writeCache(subdomain, value)
        setState({
          subdomain,
          name: value.name,
          prefecture: value.prefecture,
          loading: false,
          closed: false,
        })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const notFound = axios.isAxiosError(err) && err.response?.status === 404
        if (notFound) {
          writeCache(subdomain, null)
        }
        setState({ subdomain, name: null, prefecture: null, loading: false, closed: notFound })
      })
    return () => {
      cancelled = true
    }
  }, [subdomain, loading])

  return state
}
